import React, { useState, useEffect } from 'react';
import { useUserFeedback } from '../../hooks/useUserFeedback';
import { X, MessageCircle, Loader2, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from './button';

interface AppFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AppFeedbackFormData {
  ratingFriends?: number;
  ratingCustomers?: number;
  otherComments?: string;
}

const initialAppFeedbackData: AppFeedbackFormData = {
  ratingFriends: 0,
  ratingCustomers: 0,
  otherComments: ''
};

const RatingInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  max?: number;
}> = ({ label, value, onChange, max = 10 }) => (
  <div className="space-y-3">
    <label className="block text-sm font-medium text-rm-ink-900">{label}</label>
    <div className="inline-block">
      <div className="flex space-x-1.5 flex-wrap">
        {[...Array(max)].map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i + 1)}
            className={`w-8 h-8 rounded-full border-2 text-xs font-medium transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-rm-accent-500 ${
              value > i 
                ? 'bg-rm-accent-600 border-rm-accent-600 text-white shadow-soft' 
                : 'border-rm-neutral-600/30 text-rm-neutral-600 hover:border-rm-accent-500 hover:text-rm-accent-600 bg-white'
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-rm-neutral-600 mt-2">
        <span className="text-left">1 – Will never recommend</span>
        <span className="text-right">10 – Will recommend</span>
      </div>
    </div>
  </div>
);

export const AppFeedbackModal: React.FC<AppFeedbackModalProps> = ({ isOpen, onClose }) => {
  const {
    appFeedbackLoading,
    appFeedbackError,
    submitAppFeedback,
    clearAppFeedback,
    clearErrors
  } = useUserFeedback();

  const [appData, setAppData] = useState<AppFeedbackFormData>(initialAppFeedbackData);
  const [appSuccess, setAppSuccess] = useState(false);

  // Clear errors on mount
  useEffect(() => {
    if (isOpen) {
      clearErrors();
    }
  }, [isOpen, clearErrors]);

  if (!isOpen) return null;

  const handleAppSubmit = async () => {
    setAppSuccess(false);
    try {
      const success = await submitAppFeedback(appData);
      if (success) {
        setAppData(initialAppFeedbackData);
        clearAppFeedback();
        setAppSuccess(true);
        setTimeout(() => setAppSuccess(false), 3000);
      }
    } catch (error) {
      console.error('App feedback submission error:', error);
    }
  };

  const handleInputChange = (field: keyof AppFeedbackFormData, value: string | number) => {
    setAppData({ ...appData, [field]: value });
  };

  const handleClose = () => {
    setAppData(initialAppFeedbackData);
    setAppSuccess(false);
    clearErrors();
    onClose();
  };

  const hasChanged = JSON.stringify(appData) !== JSON.stringify(initialAppFeedbackData);
  const isValid = (appData.ratingFriends || 0) > 0 && (appData.ratingCustomers || 0) > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-card shadow-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-rm-neutral-600/20">
          <div className="flex items-center space-x-3">
            <MessageCircle className="w-6 h-6 text-rm-accent-600" />
            <h2 className="text-2xl font-bold text-rm-primary-700">App Feedback</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-rm-neutral-600 hover:text-rm-ink-900 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <RatingInput
            label="How likely are you to recommend Kim's service to a colleague?"
            value={appData.ratingFriends || 0}
            onChange={(value) => handleInputChange('ratingFriends', value)}
            max={10}
          />
          
          <RatingInput
            label="How likely are you to recommend Kim's service to a corporate customer?"
            value={appData.ratingCustomers || 0}
            onChange={(value) => handleInputChange('ratingCustomers', value)}
            max={10}
          />

          <div className="space-y-3">
            <label className="block text-sm font-medium text-rm-ink-900">
              Please, leave your advice to Kim here to become a better Buddy for you
            </label>
            <textarea
              value={appData.otherComments || ''}
              onChange={(e) => handleInputChange('otherComments', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-rm-neutral-600/30 rounded-card shadow-soft focus:outline-none focus:ring-2 focus:ring-rm-accent-500 focus:border-rm-accent-500 resize-vertical text-rm-ink-900"
              placeholder="Any additional feedback or suggestions..."
            />
          </div>
          
          {appFeedbackError && (
            <div className="flex items-center space-x-2 text-red-600 text-sm bg-red-50 rounded-card p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{appFeedbackError}</span>
            </div>
          )}
          
          {appSuccess && (
            <div className="flex items-center space-x-2 text-green-600 text-sm bg-green-50 rounded-card p-3">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>App feedback was sent successfully!</span>
            </div>
          )}
          
          <div className="text-xs text-rm-neutral-600 bg-rm-accent-300/30 rounded-card p-3 border border-rm-accent-300/50">
            We track your feedback over time to better understand how your experience evolves.
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-rm-neutral-600/20">
          <button
            onClick={handleClose}
            className="px-5 py-2 rounded-pill border border-rm-neutral-600/30 text-rm-ink-900 hover:bg-rm-bg-50 transition-colors"
          >
            Cancel
          </button>
          <Button
            onClick={handleAppSubmit}
            disabled={appFeedbackLoading || !isValid || !hasChanged}
          >
            {appFeedbackLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Send App Feedback
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
